import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const AssessmentOverlay = styled(Box)(({ open }) => ({
  position: 'fixed',
  inset: 0,
  zIndex: 9999,
  background: 'rgba(20, 42, 48, 0.75)',
  backdropFilter: 'blur(8px)',
  display: open ? 'flex' : 'none',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 10,
  animation: `${fadeIn} 0.3s ease-out`,
}));

const AssessmentPanel = styled(Box)(({ theme }) => ({
  background: '#FFFFFF',
  borderRadius: 24,
  maxWidth: 680,
  width: '100%',
  maxHeight: '90vh',
  overflowY: 'auto',
  position: 'relative',
  padding: '32px 28px 40px',
  boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  animation: `${slideUp} 0.4s ease-out`,
  [theme.breakpoints.down('sm')]: {
    padding: '20px 16px 32px',
    borderRadius: 'var(--r-lg)',
  },
}));

const CloseButton = styled(IconButton)({
  position: 'absolute',
  top: 16,
  right: 16,
  width: 36,
  height: 36,
  background: 'rgba(20,42,48,0.04)',
  transition: 'all 0.25s',
  '&:hover': {
    background: 'rgba(20,42,48,0.08)',
    transform: 'rotate(90deg)',
  },
});

const Screen = styled(Box)(({ active }) => ({
  display: active ? 'block' : 'none',
}));

const IntroHero = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
  borderRadius: 20,
  padding: '36px 32px 32px',
  textAlign: 'center',
  color: '#fff',
  marginBottom: 24,
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    padding: '28px 16px 24px',
  },
}));

const IntroIcon = styled(Box)({
  width: 56,
  height: 56,
  borderRadius: 14,
  background: 'rgba(255,255,255,0.15)',
  border: '1px solid rgba(255,255,255,0.2)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 18px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
});

const IntroTitle = styled(Typography)({
  fontFamily: "'Fraunces', serif",
  fontSize: 26,
  fontWeight: 600,
  lineHeight: 1.25,
  marginBottom: 12,
  letterSpacing: '-0.3px',
});

const IntroSub = styled(Typography)({
  fontSize: 14.5,
  color: 'rgba(255,255,255,0.8)',
  lineHeight: 1.6,
  maxWidth: 420,
  margin: '0 auto 24px',
});

const MetaRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 16,
  flexWrap: 'wrap',
  marginBottom: 24,
});

const MetaItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12.5,
  color: 'rgba(255,255,255,0.7)',
  fontWeight: 500,
});

const StartButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  padding: '13px 32px',
  borderRadius: 60,
  background: '#fff',
  color: '#117D58',
  fontFamily: "'DM Sans', sans-serif",
  fontSize: 14,
  fontWeight: 700,
  border: 'none',
  cursor: 'pointer',
  transition: 'all 0.3s',
  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
  },
});

const DimsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 12,
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
}));

const DimChip = styled(Box)({
  background: '#F2F6F5',
  border: '1px solid rgba(20,42,48,0.06)',
  borderRadius: 14,
  padding: '14px 12px',
  textAlign: 'center',
  transition: 'all 0.25s',
  '&:hover': {
    borderColor: '#1AAE7A',
    background: 'rgba(26,174,122,0.05)',
  },
});

const DimIcon = styled(Box)({
  fontSize: 24,
  marginBottom: 6,
});

const DimLabel = styled(Typography)({
  fontSize: 12,
  fontWeight: 600,
  color: '#4A6E78',
});

const ProgressBar = styled(Box)({
  marginBottom: 32,
});

const ProgressTrack = styled(Box)({
  height: 6,
  background: '#F2F6F5',
  borderRadius: 10,
  overflow: 'hidden',
  marginBottom: 12,
});

const ProgressFill = styled(Box)({
  height: '100%',
  background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
  borderRadius: 10,
  transition: 'width 0.4s ease-out',
});

const ProgressText = styled(Typography)({
  fontSize: 13,
  fontWeight: 600,
  color: '#8DABB4',
  textAlign: 'center',
});

const QuestionCard = styled(Box)({
  background: '#F2F6F5',
  borderRadius: 20,
  padding: '24px 22px',
  marginBottom: 24,
});

const QuestionCategory = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 12,
  fontWeight: 600,
  color: '#1AAE7A',
  marginBottom: 8,
  '& span:first-of-type': {
    fontSize: 16,
  },
});

const QuestionNumber = styled(Typography)({
  fontSize: 11,
  fontWeight: 600,
  color: '#8DABB4',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginBottom: 12,
});

const QuestionText = styled(Typography)({
  fontSize: 16,
  fontWeight: 600,
  color: '#142A30',
  lineHeight: 1.5,
  marginBottom: 20,
});

const LikertOptions = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
});

const LikertOption = styled(Box)(({ selected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '14px 16px',
  borderRadius: 14,
  cursor: 'pointer',
  transition: 'all 0.25s',

  border: '2px solid transparent',
  background: selected
    ? `linear-gradient(#fff, #fff) padding-box,
       linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6) border-box`
    : '#fff',

  '&:hover': {
    border: '2px solid transparent',
    background: `linear-gradient(#fff, #fff) padding-box,
                 linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6) border-box`,
  },
}));

const RadioCircle = styled(Box)(({ selected }) => ({
  width: 20,
  height: 20,
  borderRadius: '50%',
  border: `2px solid ${selected ? '#1AAE7A' : '#8DABB4'}`,
  background: selected ? '#1AAE7A' : 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  transition: 'all 0.25s',
  '&::after': {
    content: '""',
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#fff',
    opacity: selected ? 1 : 0,
    transition: 'opacity 0.25s',
  },
}));

const OptionLabel = styled(Typography)({
  flex: 1,
  fontSize: 14,
  color: '#4A6E78',
  fontWeight: 500,
});

const OptionScore = styled(Box)({
  fontSize: 12,
  fontWeight: 600,
  color: '#8DABB4',
  background: '#F2F6F5',
  padding: '4px 10px',
  borderRadius: 8,
});

const NavButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: 12,
  justifyContent: 'space-between',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const NavButton = styled('button')(({ variant, theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '12px 24px',
  borderRadius: 60,
  fontFamily: "'DM Sans', sans-serif",
  fontSize: 13.5,
  fontWeight: 600,
  border: variant === 'primary' ? 'none' : '2px solid rgba(20,42,48,0.1)',
  background: variant === 'primary' ? 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)' : 'transparent',
  color: variant === 'primary' ? '#fff' : '#4A6E78',
  cursor: 'pointer',
  transition: 'all 0.3s',
  flex: 1,
  justifyContent: 'center',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: variant === 'primary' ? '0 6px 20px rgba(26,174,122,0.3)' : '0 2px 8px rgba(0,0,0,0.05)',
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
    transform: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

const ResultsHero = styled(Box)({
  textAlign: 'center',
  marginBottom: 32,
});

const ScoreRing = styled(Box)({
  width: 160,
  height: 160,
  margin: '0 auto 24px',
  position: 'relative',
  '& svg': {
    transform: 'rotate(-90deg)',
  },
  '& .ring-bg': {
    fill: 'none',
    stroke: '#F2F6F5',
    strokeWidth: 12,
  },
  '& .ring-fill': {
    fill: 'none',
    stroke: 'url(#ringGradient)',
    strokeWidth: 12,
    strokeLinecap: 'round',
    transition: 'stroke-dashoffset 1.5s ease-out',
  },
});

const ScoreNumber = styled(Typography)({
  fontFamily: "'Fraunces', serif",
  fontSize: 48,
  fontWeight: 700,
  color: '#1AAE7A',
  lineHeight: 1,
});

const ScoreLabel = styled(Typography)({
  fontSize: 13,
  color: '#8DABB4',
  fontWeight: 500,
});

const ResultTitle = styled(Typography)({
  fontFamily: "'Fraunces', serif",
  fontSize: 28,
  fontWeight: 600,
  color: '#142A30',
  marginBottom: 10,
});

const ResultSub = styled(Typography)({
  fontSize: 15,
  color: '#4A6E78',
  lineHeight: 1.6,
  maxWidth: 480,
  margin: '0 auto',
});

const DimensionBars = styled(Box)({
  marginBottom: 32,
  '& > h3': {
    fontSize: 16,
    fontWeight: 600,
    color: '#142A30',
    marginBottom: 16,
  },
});

const DimBar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  marginBottom: 14,
});

const DimBarIcon = styled(Box)({
  fontSize: 20,
  flexShrink: 0,
});

const DimBarInfo = styled(Box)({
  flex: 1,
});

const DimBarHead = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: 6,
});

const DimBarName = styled(Typography)({
  fontSize: 13,
  fontWeight: 600,
  color: '#142A30',
});

const DimBarScore = styled(Typography)({
  fontSize: 12,
  fontWeight: 600,
  color: '#1AAE7A',
});

const DimBarTrack = styled(Box)({
  height: 8,
  background: '#F2F6F5',
  borderRadius: 10,
  overflow: 'hidden',
});

const DimBarFill = styled(Box)(({ color }) => ({
  height: '100%',
  width: 0,
  borderRadius: 10,
  transition: 'width 1s ease-out 0.3s',
  background: color === 'rose' ? 'linear-gradient(90deg, #F43F5E, #FB7185)' :
    color === 'amber' ? 'linear-gradient(90deg, #F59E0B, #FBBF24)' :
      color === 'purple' ? 'linear-gradient(90deg, #8B5CF6, #A78BFA)' :
        color === 'orange' ? 'linear-gradient(90deg, #F97316, #FB923C)' :
          color === 'blue' ? 'linear-gradient(90deg, #3B82F6, #60A5FA)' :
            'linear-gradient(90deg, #0D9488, #14B8A6)',
}));

const RecoSection = styled(Box)({
  marginBottom: 24,
  '& > h3': {
    fontSize: 16,
    fontWeight: 600,
    color: '#142A30',
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
});

const RecoCard = styled(Box)({
  background: '#F2F6F5',
  borderRadius: 20,
  padding: 20,
  display: 'flex',
  gap: 16,
  alignItems: 'center',
});

const RecoImage = styled(Box)({
  width: 100,
  height: 100,
  borderRadius: 14,
  overflow: 'hidden',
  flexShrink: 0,
  position: 'relative',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
});

const RecoBody = styled(Box)({
  flex: 1,
});

const RecoBadge = styled(Box)({
  display: 'inline-block',
  fontSize: 10,
  fontWeight: 700,
  color: '#1AAE7A',
  background: 'rgba(26,174,122,0.1)',
  padding: '4px 10px',
  borderRadius: 6,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginBottom: 8,
});

const RecoTitle = styled(Typography)({
  fontSize: 16,
  fontWeight: 600,
  color: '#142A30',
  marginBottom: 6,
});

const RecoDesc = styled(Typography)({
  fontSize: 13,
  color: '#4A6E78',
  lineHeight: 1.5,
});

const ResultActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: 12,
  justifyContent: 'center',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'center',
  },
}));

const questions = [
  { cat: 'Emotional Health', cc: 'cat-emotional', ic: '💗', t: 'Over the past two weeks, how often have you felt generally happy or content?', o: ['Rarely or never', 'Some of the time', 'About half the time', 'Most of the time', 'Almost always'] },
  { cat: 'Emotional Health', cc: 'cat-emotional', ic: '💗', t: 'How well are you able to manage difficult emotions like sadness, frustration, or anger?', o: ['Not well at all', 'Slightly well', 'Moderately well', 'Very well', 'Extremely well'] },
  { cat: 'Stress & Coping', cc: 'cat-stress', ic: '🧘', t: 'How would you rate your overall stress level in the past two weeks?', o: ['Overwhelming', 'High', 'Moderate', 'Low', 'Very low or none'] },
  { cat: 'Stress & Coping', cc: 'cat-stress', ic: '🧘', t: 'When faced with a stressful situation, how confident do you feel in your ability to cope?', o: ['Not confident at all', 'Slightly confident', 'Somewhat confident', 'Quite confident', 'Very confident'] },
  { cat: 'Sleep Quality', cc: 'cat-sleep', ic: '🌙', t: 'How would you rate the overall quality of your sleep over the past two weeks?', o: ['Very poor', 'Poor', 'Fair', 'Good', 'Excellent'] },
  { cat: 'Sleep Quality', cc: 'cat-sleep', ic: '🌙', t: 'How often do you wake up feeling rested and refreshed?', o: ['Rarely or never', 'Some of the time', 'About half the time', 'Most of the time', 'Almost always'] },
  { cat: 'Energy & Vitality', cc: 'cat-energy', ic: '⚡', t: 'How would you describe your energy levels throughout a typical day?', o: ['Very low', 'Low', 'Moderate', 'Good', 'High'] },
  { cat: 'Energy & Vitality', cc: 'cat-energy', ic: '⚡', t: 'How motivated do you feel to engage in daily activities?', o: ['Not motivated', 'Slightly', 'Somewhat', 'Quite', 'Very motivated'] },
  { cat: 'Social Well-being', cc: 'cat-social', ic: '🤝', t: 'How satisfied are you with the quality of your social connections?', o: ['Very dissatisfied', 'Dissatisfied', 'Neutral', 'Satisfied', 'Very satisfied'] },
  { cat: 'Social Well-being', cc: 'cat-social', ic: '🤝', t: 'How often do you feel supported by the people around you?', o: ['Rarely or never', 'Some of the time', 'About half the time', 'Most of the time', 'Almost always'] },
  { cat: 'Mindfulness', cc: 'cat-mindful', ic: '🧠', t: 'How often do you take time to be present and mindful during your day?', o: ['Rarely or never', 'Some of the time', 'About half the time', 'Most of the time', 'Almost always'] },
  { cat: 'Mindfulness', cc: 'cat-mindful', ic: '🧠', t: 'How aware are you of your emotional and physical state throughout the day?', o: ['Not aware', 'Slightly', 'Moderately', 'Quite', 'Very aware'] }
];

const AssessmentComponent = ({ open, onClose }) => {
  const navigate = useNavigate();

  const [screen, setScreen] = useState('intro');
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState(new Array(12).fill(-1));
  const [results, setResults] = useState(null);

  const handleStart = () => {
    setCurrentQ(0);
    setAnswers(new Array(12).fill(-1));
    setScreen('questions');
  };

  const handleAnswer = (index) => {
    const newAnswers = [...answers];
    newAnswers[currentQ] = index;
    setAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentQ < 11) {
      setCurrentQ(currentQ + 1);
    } else {
      calculateResults();
    }
  };

  const handlePrev = () => {
    if (currentQ > 0) {
      setCurrentQ(currentQ - 1);
    }
  };

  const handleRecoClick = () => {
    const title = results.recommendedTherapy.title;

    if (title === "Rejuvenate with Echo Sounds") {
      navigate("/rejuvenate-sounds");
    } else if (title === "Echo Soundscapes") {
      navigate("/echo-soundscapes");
    } else if (title === "Sound Meditation") {
      navigate("/sound-meditation");
    }
  };

  const calculateResults = () => {
    const dims = [
      { n: 'Emotional Health', ic: '💗', fc: 'rose', qs: [0, 1] },
      { n: 'Stress & Coping', ic: '🧘', fc: 'amber', qs: [2, 3] },
      { n: 'Sleep Quality', ic: '🌙', fc: 'purple', qs: [4, 5] },
      { n: 'Energy & Vitality', ic: '⚡', fc: 'orange', qs: [6, 7] },
      { n: 'Social Well-being', ic: '🤝', fc: 'blue', qs: [8, 9] },
      { n: 'Mindfulness', ic: '🧠', fc: 'teal', qs: [10, 11] }
    ];

    let total = 0;
    const dimScores = dims.map(d => {
      const score = d.qs.reduce((s, q) => s + (answers[q] + 1), 0);
      total += score;
      return { ...d, score, pct: (score / 10) * 100 };
    });

    const sorted = [...dimScores].sort((a, b) => a.score - b.score);
    // const weakest = sorted[0].n;
    const minScore = Math.min(...dimScores.map(d => d.score));
    const weakestDims = dimScores.filter(d => d.score === minScore);
    const weakest = weakestDims[Math.floor(Math.random() * weakestDims.length)].n;

    const therapyMap = {
      'Sleep Quality': 'rejuvenate',
      'Emotional Health': 'rejuvenate',
      'Stress & Coping': 'soundscapes',
      'Social Well-being': 'soundscapes',
      'Energy & Vitality': 'meditation',
      'Mindfulness': 'meditation'
    };

    const therapies = {
      rejuvenate: {
        title: 'Rejuvenate with Echo Sounds',
        desc: 'Therapeutic solfeggio frequencies from 174Hz to 963Hz to restore emotional balance and deep healing.',
        img: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=300&h=300&fit=crop&q=80'
      },
      soundscapes: {
        title: 'Echo Soundscapes',
        desc: 'Immersive ambient landscapes blending nature with healing tones to dissolve stress and create deep calm.',
        img: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=300&h=300&fit=crop&q=80'
      },
      meditation: {
        title: 'Sound Meditation',
        desc: 'Guided sessions with binaural beats to sharpen focus, boost energy, and cultivate mindful awareness.',
        img: 'https://images.unsplash.com/photo-1508672019048-805c876b67e2?w=300&h=300&fit=crop&q=80'
      }
    };

    const recommendedTherapy = therapies[therapyMap[weakest] || therapies['rejuvenate']];

    setResults({
      total,
      dimScores,
      recommendedTherapy
    });
    setScreen('results');
  };

  const handleRestart = () => {
    setScreen('intro');
    setCurrentQ(0);
    setAnswers(new Array(12).fill(-1));
    setResults(null);
  };

  const handleClose = () => {
    handleRestart();
    onClose();
  };

  const q = questions[currentQ];
  const progress = ((currentQ + 1) / 12) * 100;

  return (
    <AssessmentOverlay open={open} onClick={handleClose}>
      <AssessmentPanel onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </CloseButton>

        {/* INTRO SCREEN */}
        <Screen active={screen === 'intro'}>
          <IntroHero>
            <IntroIcon>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
              </svg>
            </IntroIcon>
            <IntroTitle>
              Well-Being Self-Assessment
            </IntroTitle>
            <IntroSub>
              A quick check-in across 6 wellness dimensions — get a personalised sound therapy recommendation.
            </IntroSub>
            <MetaRow>
              <MetaItem>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
                5–7 min
              </MetaItem>
              <MetaItem>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M9 11l3 3L22 4" />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
                12 Questions
              </MetaItem>
              <MetaItem>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                Private
              </MetaItem>
            </MetaRow>
            <StartButton onClick={handleStart}>
              Begin Assessment
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14" />
                <path d="M12 5l7 7-7 7" />
              </svg>
            </StartButton>
          </IntroHero>

          <DimsGrid>
            <DimChip><DimIcon>💗</DimIcon><DimLabel>Emotional</DimLabel></DimChip>
            <DimChip><DimIcon>🧘</DimIcon><DimLabel>Stress</DimLabel></DimChip>
            <DimChip><DimIcon>🌙</DimIcon><DimLabel>Sleep</DimLabel></DimChip>
            <DimChip><DimIcon>⚡</DimIcon><DimLabel>Energy</DimLabel></DimChip>
            <DimChip><DimIcon>🤝</DimIcon><DimLabel>Social</DimLabel></DimChip>
            <DimChip><DimIcon>🧠</DimIcon><DimLabel>Mindfulness</DimLabel></DimChip>
          </DimsGrid>
        </Screen>

        {/* QUESTIONS SCREEN */}
        <Screen active={screen === 'questions'}>
          <ProgressBar>
            <ProgressTrack>
              <ProgressFill style={{ width: `${progress}%` }} />
            </ProgressTrack>
            <ProgressText>{currentQ + 1} / 12</ProgressText>
          </ProgressBar>

          <QuestionCard>
            <QuestionCategory>
              <span>{q.ic}</span> {q.cat}
            </QuestionCategory>
            <QuestionNumber>Question {currentQ + 1} of 12</QuestionNumber>
            <QuestionText>{q.t}</QuestionText>

            <LikertOptions>
              {q.o.map((option, i) => (
                <LikertOption key={i} selected={answers[currentQ] === i} onClick={() => handleAnswer(i)}>
                  <RadioCircle selected={answers[currentQ] === i} />
                  <OptionLabel>{option}</OptionLabel>
                  <OptionScore>{i + 1}</OptionScore>
                </LikertOption>
              ))}
            </LikertOptions>
          </QuestionCard>

          <NavButtons>
            <NavButton onClick={handlePrev} style={{ visibility: currentQ === 0 ? 'hidden' : 'visible' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5" />
                <path d="M12 19l-7-7 7-7" />
              </svg>
              Back
            </NavButton>
            <NavButton variant="primary" onClick={handleNext} disabled={answers[currentQ] === -1}>
              {currentQ === 11 ? 'See Results' : 'Next'}
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14" />
                <path d="M12 5l7 7-7 7" />
              </svg>
            </NavButton>
          </NavButtons>
        </Screen>

        {/* RESULTS SCREEN */}
        <Screen active={screen === 'results'}>
          {results && (
            <>
              <ResultsHero>
                <ScoreRing>
                  <svg viewBox="0 0 160 160">
                    <defs>
                      <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#22c55e" />
                        <stop offset="50%" stopColor="#14b8a6" />
                        <stop offset="100%" stopColor="#8b5cf6" />
                      </linearGradient>
                    </defs>
                    <circle className="ring-bg" cx="80" cy="80" r="70" />
                    <circle
                      className="ring-fill"
                      cx="80"
                      cy="80"
                      r="70"
                      strokeDasharray={2 * Math.PI * 70}
                      strokeDashoffset={2 * Math.PI * 70 * (1 - results.total / 60)}
                    />
                  </svg>
                  <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <ScoreNumber>{results.total}</ScoreNumber>
                    <ScoreLabel>out of 60</ScoreLabel>
                  </Box>
                </ScoreRing>
                <ResultTitle>
                  {results.total >= 48 ? 'Excellent Wellness!' :
                    results.total >= 36 ? 'Good Wellness' :
                      results.total >= 24 ? 'Moderate Wellness' : 'Needs Attention'}
                </ResultTitle>
                <ResultSub>
                  {results.total >= 48 ? "You're thriving! Keep nurturing with regular sound therapy." :
                    results.total >= 36 ? 'Doing well overall. Targeted therapy can strengthen weaker areas.' :
                      results.total >= 24 ? 'Room to improve. A consistent sound therapy routine will help.' :
                        'Several areas need support. Daily sessions on your weakest dimension are recommended.'}
                </ResultSub>
              </ResultsHero>

              <DimensionBars>
                <h3>Scores by Dimension</h3>
                {results.dimScores.map((dim, i) => (
                  <DimBar key={i}>
                    <DimBarIcon>{dim.ic}</DimBarIcon>
                    <DimBarInfo>
                      <DimBarHead>
                        <DimBarName>{dim.n}</DimBarName>
                        <DimBarScore>{dim.score}/10</DimBarScore>
                      </DimBarHead>
                      <DimBarTrack>
                        <DimBarFill color={dim.fc} style={{ width: `${dim.pct}%` }} />
                      </DimBarTrack>
                    </DimBarInfo>
                  </DimBar>
                ))}
              </DimensionBars>

              <RecoSection>
                <h3>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1AAE7A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18V5l12-2v13" />
                    <circle cx="6" cy="18" r="3" />
                    <circle cx="18" cy="16" r="3" />
                  </svg>
                  Your Recommended Therapy
                </h3>
                <RecoCard onClick={handleRecoClick} sx={{ cursor: "pointer" }}>
                  <RecoImage>
                    <img src={results.recommendedTherapy.img} alt={results.recommendedTherapy.title} />
                  </RecoImage>
                  <RecoBody>
                    <RecoBadge>Best for You</RecoBadge>
                    <RecoTitle>{results.recommendedTherapy.title}</RecoTitle>
                    <RecoDesc>{results.recommendedTherapy.desc}</RecoDesc>
                  </RecoBody>
                </RecoCard>
              </RecoSection>

              <ResultActions>
                <NavButton onClick={handleRestart}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="1 4 1 10 7 10" />
                    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                  </svg>
                  Retake
                </NavButton>
                <NavButton variant="primary" onClick={handleClose}>
                  Back to Therapies
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14" />
                    <path d="M12 5l7 7-7 7" />
                  </svg>
                </NavButton>
              </ResultActions>
            </>
          )}
        </Screen>
      </AssessmentPanel>
    </AssessmentOverlay>
  );
};

export default AssessmentComponent;
